import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import useAuthStore from '../stores/authStore';
import useProviderProfileStore from '../stores/providerProfileStore';
import useStaffProfileStore from '../stores/staffProfileStore';
import { UserRole, USER_ROLE } from '../types/auth';
import { AppLoader } from './ui';

interface ProtectedRouteProps {
	children: React.ReactNode;
	allowedRoles?: UserRole[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
	const { isAuthenticated, user, isInitialized } = useAuthStore();
	const { profile: providerProfile, profileError: providerProfileError } =
		useProviderProfileStore();
	const { profile: staffProfile, profileError: staffProfileError } = useStaffProfileStore();
	const location = useLocation();

	// Wait for auth to be initialized before making any redirect decisions
	if (!isInitialized) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	if (!isAuthenticated || !user) {
		return <Navigate to="/login" replace />;
	}

	// Check if user needs to change password (first-time login)
	const isStaff = user.role === USER_ROLE.STAFF;
	const changePasswordPath = isStaff ? '/staff/change-password' : '/providers/change-password';
	const isOnChangePasswordPage = location.pathname === changePasswordPath;

	if (user.passwordChangeRequired && !isOnChangePasswordPage) {
		return <Navigate to={changePasswordPath} replace />;
	}

	// For Salon Owner users, wait for provider profile to load before rendering.
	// Skip on the change-password page which doesn't need profile data.
	// Allow rendering if there's an error (pages can show their own error states).
	if (
		user.role === USER_ROLE.SALON_OWNER &&
		!isOnChangePasswordPage &&
		!providerProfile &&
		!providerProfileError
	) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	// For Staff users, wait for staff profile to load before rendering.
	if (isStaff && !isOnChangePasswordPage && !staffProfile && !staffProfileError) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	if (allowedRoles && !allowedRoles.includes(user.role)) {
		// Redirect based on role
		if (user.role === USER_ROLE.SALON_OWNER) {
			return <Navigate to="/providers/dashboard" replace />;
		}
		if (isStaff) {
			return <Navigate to="/staff/dashboard" replace />;
		}
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
};

export default ProtectedRoute;
